import type Controller from '@ember/controller';
import { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBreadcrumb,
  PrismButton,
  PrismCard,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsRecommendationsCompetitorsImpactPicker from 'lumen/components/settings/recommendations/competitors-impact-picker';
import SettingsRecommendationsCreateSeasonalStrategyButton from 'lumen/components/settings/recommendations/create-seasonal-strategy-button';
import SettingsRecommendationsStrategyCard from 'lumen/components/settings/recommendations/strategy-card';
import type { RecommendationStrategy } from 'lumen/models';
import type {
  CompsetPositioningDayOfWeekSettings,
  CompsetPositioningSettings,
} from 'lumen/models/recommendation-strategy';
import type { RecommendationsModel } from 'lumen/routes/hotel/settings/recommendations';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY } from 'lumen/utils/constants/pricing-assistant-constants';
import {
  sortSeasonalStrategies,
  updateCompetitorImpactForSpecifiedDays,
} from 'lumen/utils/pricing-assistant/recommendation-settings';

interface SettingsRecommendationsCompetitorsImpactSignature {
  Args: {
    model: RecommendationsModel;
  };
}

export default class SettingsRecommendationsCompetitorsImpact extends Component<SettingsRecommendationsCompetitorsImpactSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare usageAnalytics: Services['usage-analytics'];

  @controller('hotel.settings.recommendations')
  declare recommendationsController: Controller & {
    strategiesPerType: { competitorImpactStrategies: RecommendationStrategy[] };
  };

  @tracked isSomeStrategyCardBeingEdited = false;
  @tracked isUserDefiningImpact = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get competitorsImpactStrategies() {
    return this.recommendationsController.strategiesPerType
      .competitorImpactStrategies;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultStrategy() {
    // There should be only one default strategy
    return this.competitorsImpactStrategies.find(
      (strategy) => strategy.isDefaultStrategy,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsLeadingRoomName() {
    return this.hotelAndMarketService.hotel.caxxxPmsSettings
      .pms_id_to_booking_room_name_mapping[
      this.hotelAndMarketService.hotel.caxxxPmsSettings.pms_leading_room_id
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get seasonalStrategies() {
    return sortSeasonalStrategies(this.competitorsImpactStrategies);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showEmptyState() {
    // isUserDefiningImpact means that the user has clicked the CTA that he wishes to assign initial importances to competitors
    if (this.isUserDefiningImpact) {
      // if cancel was clicked during the initial assignment state we should show back the empty state (no card being edited)
      if (
        !this.isSomeStrategyCardBeingEdited &&
        this.defaultStrategy?.isCompsetPositioningStartingState
      ) {
        return true;
      }

      return false;
    }

    // show empty state if difference between createdAt and updatedAt is less than 1 second
    // currently this is the only way to tell if a default strategy is defined by BE or by the user
    if (this.defaultStrategy) {
      return this.defaultStrategy.isCompsetPositioningStartingState;
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get strategyComponent() {
    return SettingsRecommendationsCompetitorsImpactPicker;
  }

  @action onDefineCompetitorsImpactClicked(): void {
    // Reset values on our side in case the user wants to go to the initial assignment of competitors
    // This is so that we can keep required validation in the recommendation-strategy model
    if (this.defaultStrategy) {
      const defaultImpactedDays = [1, 2, 3, 4, 5, 6, 7];

      const daySettingsObject = (
        this.defaultStrategy.settings as CompsetPositioningSettings
      ).day_of_week[
        defaultImpactedDays[0] as number
      ] as CompsetPositioningDayOfWeekSettings;

      updateCompetitorImpactForSpecifiedDays(
        this.defaultStrategy,
        Object.keys(daySettingsObject),
        COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY,
        defaultImpactedDays,
      );

      this.usageAnalytics.track('Default Competitor impact creation', {
        eventTrigger: 'recommendation-settings',
        product: 'pricing-manager',
      });
    }

    this.isUserDefiningImpact = true;
    this.setSomeStrategyCardBeingEditedState(true);
  }

  @action setSomeStrategyCardBeingEditedState(state: boolean): void {
    this.isSomeStrategyCardBeingEdited = state;
  }

  <template>
    <SettingsPageTemplate
      @description={{t
        "pricing_assistant.recommendations.settings.competitor_impact.description"
      }}
      @title={{t
        "pricing_assistant.recommendations.settings.competitor_impact.title"
      }}
      @type="container"
    >
      <:breadcrumb>
        <PrismBreadcrumb as |B|>
          <B.item
            @label={{t "pricing_assistant.recommendations.settings.main_title"}}
            @route="hotel.settings.recommendations.index"
          />
          <B.item
            @isCurrent={{true}}
            @label={{t
              "pricing_assistant.recommendations.settings.competitor_impact.title"
            }}
          />
        </PrismBreadcrumb>
      </:breadcrumb>
      <:link>
        <a
          class="prism-link"
          href="https://help.mylighthouse.com/en/articles/7857322-how-do-i-customize-my-recommendations"
          rel="noopener noreferrer"
          target="_blank"
        >{{t "general.learn_more"}}</a>
      </:link>
      <:default>
        <div class="seasonal-settings-wrapper">
          {{#if this.showEmptyState}}
            <PrismCard class="flex items-center justify-center pt-400">
              <:content>
                <PrismMessage
                  @description={{t
                    "pricing_assistant.recommendations.competitors_impact.empty_state.description"
                  }}
                  @illustration="100x100_balance"
                  @title={{t
                    "pricing_assistant.recommendations.competitors_impact.empty_state.title"
                  }}
                >
                  <:primaryAction>
                    <PrismButton
                      @buttonType="primary"
                      @isDisabled={{this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled}}
                      @label={{t
                        "pricing_assistant.recommendations.competitors_impact.define_competitors_impact"
                      }}
                      @onClick={{this.onDefineCompetitorsImpactClicked}}
                    />
                  </:primaryAction>
                </PrismMessage>
              </:content>
            </PrismCard>
          {{else}}
            {{#if this.defaultStrategy}}
              <div class="pb-400">
                <SettingsRecommendationsStrategyCard
                  @initiallyOpened={{true}}
                  @isSomeStrategyCardBeingEdited={{this.isSomeStrategyCardBeingEdited}}
                  @pmsLeadingRoomName={{this.pmsLeadingRoomName}}
                  @saveStrategyTask={{@model.saveStrategyTask}}
                  @seasonalStrategies={{this.seasonalStrategies}}
                  @setSomeStrategyCardBeingEditedState={{this.setSomeStrategyCardBeingEditedState}}
                  @strategy={{this.defaultStrategy}}
                  @strategyComponent={{this.strategyComponent}}
                />
              </div>

              {{#if this.seasonalStrategies.length}}
                <div class="flex flex-col gap-200">
                  <div class="flex flex-row items-center justify-between">
                    <h3 class="text-neutral-emphasis">{{t
                        "pricing_assistant.recommendations.price_range.subtitle.seasonal_strategies"
                      }}</h3>

                    {{#unless
                      this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
                    }}
                      <SettingsRecommendationsCreateSeasonalStrategyButton
                        @defaultStrategy={{this.defaultStrategy}}
                        @isSomeStrategyCardBeingEdited={{this.isSomeStrategyCardBeingEdited}}
                        @seasonalStrategies={{this.seasonalStrategies}}
                        @setSomeStrategyCardBeingEditedState={{this.setSomeStrategyCardBeingEditedState}}
                        @settingsType="compset_positioning"
                      />
                    {{/unless}}
                  </div>
                  {{#each this.seasonalStrategies as |strategy|}}
                    <SettingsRecommendationsStrategyCard
                      @defaultStrategy={{this.defaultStrategy}}
                      @isSomeStrategyCardBeingEdited={{this.isSomeStrategyCardBeingEdited}}
                      @saveStrategyTask={{@model.saveStrategyTask}}
                      @seasonalStrategies={{this.seasonalStrategies}}
                      @setSomeStrategyCardBeingEditedState={{this.setSomeStrategyCardBeingEditedState}}
                      @strategy={{strategy}}
                      @strategyComponent={{this.strategyComponent}}
                    />
                  {{/each}}
                </div>
              {{else}}
                {{#unless
                  this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
                }}
                  <PrismCard class="flex items-center justify-center">
                    <:content>
                      {{#if
                        this.defaultStrategy.isCompsetPositioningStartingState
                      }}
                        <PrismMessage
                          @description={{t
                            "pricing_assistant.recommendations.competitors_impact.empty_state.starting_state_description"
                          }}
                          @illustration="100x100_chart"
                          @title={{t
                            "pricing_assistant.recommendations.empty_state.title"
                          }}
                        />
                      {{else}}
                        <PrismMessage
                          @description={{t
                            "pricing_assistant.recommendations.competitors_impact.empty_state.non_starting_state_description"
                          }}
                          @illustration="100x100_chart"
                          @title={{t
                            "pricing_assistant.recommendations.empty_state.title"
                          }}
                        >
                          <:primaryAction>
                            <SettingsRecommendationsCreateSeasonalStrategyButton
                              @defaultStrategy={{this.defaultStrategy}}
                              @isSomeStrategyCardBeingEdited={{this.isSomeStrategyCardBeingEdited}}
                              @seasonalStrategies={{this.seasonalStrategies}}
                              @setSomeStrategyCardBeingEditedState={{this.setSomeStrategyCardBeingEditedState}}
                              @settingsType="compset_positioning"
                            />
                          </:primaryAction>
                        </PrismMessage>
                      {{/if}}
                    </:content>
                  </PrismCard>
                {{/unless}}
              {{/if}}
            {{/if}}
          {{/if}}
        </div>
      </:default>
    </SettingsPageTemplate>
  </template>
}
